<template>
  <div
    data-test="rate-card-alert"
    class="tw-flex tw-flex-col tw-gap-3 tw-rounded tw-border tw-border-solid tw-bg-white tw-py-3 tw-pl-4 tw-pr-3"
    :class="guidance.hasMatch ? 'tw-border-indigo-200' : 'tw-border-gray-300'"
  >
    <div class="tw-flex tw-min-h-[28px] tw-items-center tw-gap-4">
      <div class="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2.5">
        <WdsIcon
          name="tag"
          :size="16"
          class="tw-shrink-0"
          :class="guidance.hasMatch ? 'tw-text-indigo-500' : 'tw-text-gray-700'"
        />

        <div class="tw-flex tw-min-w-0 tw-flex-wrap tw-items-baseline tw-gap-x-3 tw-gap-y-1 tw-text-base">
          <span v-if="guidance.hasMatch" class="tw-text-gray-900">
            {{ guidance.introText }}
            <span class="tw-font-medium tw-tabular-nums">{{ guidance.rangeAmountText }}</span>
            <span v-if="guidance.unitText" class="tw-ml-1 tw-whitespace-nowrap">{{ guidance.unitText }}</span>
          </span>
          <span v-else class="tw-text-gray-800">{{ guidance.reasonDescription }}</span>

          <WdsButtonText v-if="guidance.hasMatch" data-test="rate-card-why-toggle" @click="expanded = !expanded">
            {{ expanded ? $t('Hide') : $t('Why?') }}
          </WdsButtonText>
        </div>
      </div>

      <span
        v-if="guidance.hasMatch && guidance.excludesMspFee"
        class="tw-whitespace-nowrap tw-text-sm tw-text-gray-700"
      >
        {{ $t('Excluding the MSP’s fee') }}
      </span>

      <template v-if="guidance.hasMatch && showApply">
        <WdsButton v-if="!applied" secondary sm data-test="rate-card-apply" @click="$emit('apply')">
          {{ $t('Apply') }}
        </WdsButton>
        <WdsButton v-else sm disabled data-test="rate-card-applied">
          <WdsStack align-center gap-sm>
            <WdsIcon name="checkmark" :size="16" />
            {{ $t('Applied') }}
          </WdsStack>
        </WdsButton>
        <WdsButtonIcon
          v-if="applied && canUndo"
          sm
          transparent-background
          type="button"
          :aria-label="$t('Undo applying the rate card')"
          data-test="rate-card-undo"
          @click="$emit('undo')"
        >
          <WdsIcon name="undo" :size="16" class="tw-text-gray-700" />
        </WdsButtonIcon>
      </template>
    </div>

    <div
      v-if="guidance.hasMatch && expanded"
      class="tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-3.5 tw-border-0 tw-border-t tw-border-solid tw-border-indigo-100 tw-pb-0.5 tw-pt-3.5"
    >
      <div
        v-if="guidance.criteria.length || guidance.validUntilText"
        class="tw-col-span-2 tw-flex tw-items-baseline tw-justify-between tw-gap-3"
        :class="guidance.criteria.length ? 'tw-border-0 tw-border-b tw-border-solid tw-border-gray-100 tw-pb-3' : ''"
      >
        <div v-if="guidance.criteria.length" class="tw-flex tw-items-center tw-gap-2">
          <p class="tw-m-0 tw-text-base tw-font-medium tw-leading-tight tw-text-gray-900">
            {{ $t('Matched on') }}
          </p>
          <WdsTooltip global placement="top" :text="guidance.tooltipText">
            <WdsIcon name="info" :size="16" class="tw-text-gray-700" data-test="rate-card-info-trigger" />
          </WdsTooltip>
        </div>
        <p v-if="guidance.validUntilText" class="tw-m-0 tw-whitespace-nowrap tw-text-sm tw-leading-tight tw-text-gray-700">
          {{ guidance.validUntilText }}
        </p>
      </div>
      <div
        v-for="(criterion, index) in guidance.criteria"
        :key="index"
        class="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5"
      >
        <span class="tw-text-sm tw-leading-4 tw-text-gray-700">{{ criterion.label }}</span>
        <span class="tw-text-[13px] tw-font-medium tw-leading-[18px] tw-text-gray-900">{{ criterion.value }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { WdsButton, WdsButtonIcon, WdsButtonText, WdsIcon, WdsStack, WdsTooltip } from '@worksome/wds-components'
import { ref } from 'vue'
import { $t } from '@/composables/translation'
import { RateCardGuidance } from '@/components/Job/composables/useRateCardBandGuidance'

defineProps<{
  guidance: RateCardGuidance
  // Apply is offered in rate-range mode only — specific-rate has no midpoint to apply.
  showApply?: boolean
  applied?: boolean
  canUndo?: boolean
}>()

defineEmits<{ (e: 'apply'): void; (e: 'undo'): void }>()

const expanded = ref(false)
</script>
